import { errorToast } from "@design-system/components/toast";
import { userAtom } from "@framework/config/atoms";
import { trans } from "@mongez/localization";
import { atom, ReactAtom } from "@mongez/react-atom";

export type PortalActions<T = any> = {
  open: (data?: T) => void;
  close: () => void;
  toggle: (data?: T) => void;
  useOpened: () => boolean;
  useData: () => T;
};

type PortalData<T = any> = {
  opened: boolean;
  data: T;
};

export function createPortal<T = any>(name: string, opened: boolean = false) {
  return atom<PortalData<T>, PortalActions<T>>({
    key: `${name}-portal`,
    default: {
      opened: opened,
      data: {} as T,
    },
    actions: {
      open(data?: T) {
        this.merge({
          opened: true,
          data,
        });
      },
      close() {
        this.change("opened", false);
      },
      toggle(data?: T) {
        const opened = this.get("opened");

        if (opened) {
          return this.change("opened", false);
        }

        this.merge({
          opened: true,
          data,
        });
      },
      useOpened() {
        return (this as ReactAtom).use("opened");
      },
      useData() {
        return (this as ReactAtom).use("data");
      },
    },
  });
}

export function createGuardedPortal<T = any>(
  name: string,
  opened: boolean = false,
) {
  return atom<PortalData<T>, PortalActions<T>>({
    key: `${name}-portal`,
    default: {
      opened: opened,
      data: {} as T,
    },
    actions: {
      open(data?: T) {
        if (userAtom.get("userType") !== "user") {
          errorToast(trans("logInFirstPlease"));
          return;
        }
        this.merge({
          opened: true,
          data,
        });
      },
      close() {
        this.change("opened", false);
      },
      toggle(data?: T) {
        const opened = this.get("opened");

        if (opened) {
          return this.change("opened", false);
        }

        (this as ReactAtom).open(data);
      },
      useOpened() {
        return (this as ReactAtom).use("opened");
      },
      useData() {
        return (this as ReactAtom).use("data");
      },
    },
  });
}
